import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Search,
  Monitor,
  Calendar,
  Clock,
  Activity,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  BarChart2,
  Users,
  Download,
} from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const StatsCard = ({ icon: Icon, label, value, subtext, color = "blue" }) => {
  const colorClasses = {
    blue: "from-blue-500/20 to-blue-600/20 border-blue-500/30",
    purple: "from-purple-500/20 to-purple-600/20 border-purple-500/30",
    green: "from-green-500/20 to-green-600/20 border-green-500/30",
    orange: "from-orange-500/20 to-orange-600/20 border-orange-500/30",
  };

  const iconColors = {
    blue: "text-blue-400",
    purple: "text-purple-400",
    green: "text-green-400",
    orange: "text-orange-400",
  };

  return (
    <div
      className={`relative overflow-hidden rounded-xl border bg-gradient-to-br ${colorClasses[color]} backdrop-blur-sm p-6 transition-all hover:scale-[1.02]`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
            {label}
          </p>
          <p className="text-3xl font-bold text-white mb-1">{value}</p>
          {subtext && <p className="text-xs text-slate-400">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-lg bg-white/5 ${iconColors[color]}`}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
};

const exportToExcel = ({ dailyData, selectedDate, deviceId }) => {
  const rows = [];

  dailyData.forEach((app) => {
    if (!app.titles || app.titles.length === 0) {
      rows.push({
        Date: selectedDate,
        Application: app.app,
        "Window Title": "-",
        Duration: "-",
        "Total Duration": app.totalDuration,
      });
    } else {
      app.titles.forEach((t) => {
        rows.push({
          Date: selectedDate,
          Application: app.app,
          "Window Title": t.title,
          Duration: t.duration,
          "Total Duration": app.totalDuration,
        });
      });
    }
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, "Activity Report");

  const buffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const blob = new Blob([buffer], {
    type: "application/octet-stream",
  });

  saveAs(blob, `Activity_Report_${deviceId}_${selectedDate}.xlsx`);
};

const DataTable = ({ data }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortField, setSortField] = useState("duration");
  const [sortDirection, setSortDirection] = useState("desc");

  const toggleRow = (app) => {
    const newRows = new Set(expandedRows);
    if (newRows.has(app)) {
      newRows.delete(app);
    } else {
      newRows.add(app);
    }
    setExpandedRows(newRows);
  };

  const parseDuration = (duration) => {
    const parts = duration.match(/(\d+)h|(\d+)m|(\d+)s/g) || [];
    let seconds = 0;
    parts.forEach((part) => {
      if (part.includes("h")) seconds += parseInt(part) * 3600;
      if (part.includes("m")) seconds += parseInt(part) * 60;
      if (part.includes("s")) seconds += parseInt(part);
    });
    return seconds;
  };

  const sortedData = [...data].sort((a, b) => {
    if (sortField === "duration") {
      const aSeconds = parseDuration(a.totalDuration);
      const bSeconds = parseDuration(b.totalDuration);
      return sortDirection === "asc"
        ? aSeconds - bSeconds
        : bSeconds - aSeconds;
    }
    if (sortField === "app") {
      return sortDirection === "asc"
        ? a.app.localeCompare(b.app)
        : b.app.localeCompare(a.app);
    }
    return 0;
  });

  const maxDuration =
    sortedData.length > 0 ? parseDuration(sortedData[0].totalDuration) : 0;

  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-slate-900/50 backdrop-blur-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 bg-slate-800/50">
              <th className="px-6 py-4 text-left">
                <div className="w-8"></div>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => {
                    if (sortField === "app") {
                      setSortDirection(
                        sortDirection === "asc" ? "desc" : "asc",
                      );
                    } else {
                      setSortField("app");
                      setSortDirection("asc");
                    }
                  }}
                  className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-300 transition-colors"
                >
                  Application
                  {sortField === "app" && (
                    <span className="text-blue-400">
                      {sortDirection === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => {
                    if (sortField === "duration") {
                      setSortDirection(
                        sortDirection === "asc" ? "desc" : "asc",
                      );
                    } else {
                      setSortField("duration");
                      setSortDirection("desc");
                    }
                  }}
                  className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-300 transition-colors"
                >
                  Duration
                  {sortField === "duration" && (
                    <span className="text-blue-400">
                      {sortDirection === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </button>
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                Windows
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                Activity
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, idx) => {
              const isExpanded = expandedRows.has(item.app);
              const duration = parseDuration(item.totalDuration);
              const percentage =
                maxDuration > 0
                  ? Math.round((duration / maxDuration) * 100)
                  : 0;

              return (
                <React.Fragment key={idx}>
                  <tr
                    className={`border-b border-white/5 transition-colors ${isExpanded ? "bg-slate-800/30" : "hover:bg-slate-800/20"}`}
                  >
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleRow(item.app)}
                        className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-slate-400 hover:text-slate-300"
                      >
                        {isExpanded ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 text-white font-bold text-sm">
                          {item.app.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-white">
                          {item.app}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 font-mono text-sm font-semibold">
                        {item.totalDuration}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium">
                        {item.titles?.length || 0} windows
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-slate-700/50 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-mono font-semibold text-slate-400 min-w-[45px] text-right">
                          {percentage}%
                        </span>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="border-b border-white/5">
                      <td colSpan="5" className="px-6 py-6 bg-slate-800/20">
                        <div className="pl-12">
                          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">
                            <Activity size={14} />
                            <span>Window Activity Breakdown</span>
                          </div>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {item.titles?.map((t, i) => (
                              <div
                                key={i}
                                className="flex items-start justify-between gap-4 p-4 rounded-lg bg-slate-900/50 border border-white/5 hover:border-white/10 transition-colors"
                              >
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-700/50 text-slate-400 text-xs font-semibold flex-shrink-0">
                                    {i + 1}
                                  </div>
                                  <p className="text-sm text-slate-300 line-clamp-2 flex-1">
                                    {t.title}
                                  </p>
                                </div>
                                <span className="text-sm font-mono font-semibold text-blue-400 flex-shrink-0">
                                  {t.duration}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const UserDashboard = () => {
  const [deviceId, setDeviceId] = useState("");
  const [isLogged, setIsLogged] = useState(false);
  const [dailyData, setDailyData] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  const handleLogin = (e) => {
    e.preventDefault();
    if (deviceId.trim()) {
      setIsLogged(true);
      fetchData(deviceId, selectedDate);
    }
  };

  const fetchData = async (id, date) => {
    setLoading(true);
    setError("");
    try {
      const [dailyRes, timelineRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/daily?deviceId=${id}&date=${date}`),
        axios.get(`${API_BASE_URL}/timeline?deviceId=${id}&date=${date}`),
      ]);
      setDailyData(dailyRes.data);
      setTimelineData(timelineRes.data);
    } catch (err) {
      setError(
        "Failed to fetch data. Please check if the device ID is correct and the backend is running.",
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLogged) {
      fetchData(deviceId, selectedDate);
    }
  }, [selectedDate, isLogged, deviceId]);

  // Calculate statistics
  const parseDuration = (duration) => {
    const parts = duration.match(/(\d+)h|(\d+)m|(\d+)s/g) || [];
    let seconds = 0;
    parts.forEach((part) => {
      if (part.includes("h")) seconds += parseInt(part) * 3600;
      if (part.includes("m")) seconds += parseInt(part) * 60;
      if (part.includes("s")) seconds += parseInt(part);
    });
    return seconds;
  };

  const totalApps = dailyData.length;
  const totalSeconds = dailyData.reduce(
    (acc, item) => acc + parseDuration(item.totalDuration),
    0,
  );
  const totalHours = Math.floor(totalSeconds / 3600);
  const totalMinutes = Math.floor((totalSeconds % 3600) / 60);
  const totalWindows = dailyData.reduce(
    (acc, item) => acc + (item.titles?.length || 0),
    0,
  );
  const mostUsedApp = dailyData[0]?.app || "N/A";

  if (!isLogged) {
    return (
      <div className="flex justify-center items-center min-h-[90vh] animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="p-12 w-full max-w-[450px] text-center glass-card">
          <Monitor className="text-primary mx-auto mb-6" size={48} />
          <h1 className="text-3xl font-bold mb-2">Access Your Dashboard</h1>
          <p className="text-slate-400 mb-8">
            Enter your Device ID to view your activity records
          </p>
          <form onSubmit={handleLogin} className="flex flex-col gap-6">
            <div className="relative flex items-center">
              <Search className="absolute left-4 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Enter Device ID"
                className="input-field"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn-primary">
              View Insights <Search size={20} />
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8">
      <div className="w-full max-w-none">
        {/* Header */}
        <header className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-8 pb-8 border-b border-white/10">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-2">
              <h1 className="text-4xl font-bold text-white">
                Activity Analytics
              </h1>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/50 border border-white/10">
                <Monitor size={14} className="text-slate-400" />
                <span className="text-xs font-mono text-slate-400">
                  {deviceId}
                </span>
              </div>
            </div>
            <p className="text-slate-400">
              {new Date(selectedDate).toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Date Picker */}
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/50 border border-white/10">
              <Calendar size={18} className="text-slate-400" />
              <input
                type="date"
                className="bg-transparent border-none text-white outline-none font-medium"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>

            {/* Download Excel */}
            {dailyData.length > 0 && (
              <button
                onClick={() =>
                  exportToExcel({
                    dailyData,
                    selectedDate,
                    deviceId,
                  })
                }
                className="flex items-center gap-2 px-5 py-3 rounded-xl 
                 bg-green-600/20 border border-green-500/30 
                 text-green-400 hover:bg-green-600/30 
                 disabled:opacity-50 disabled:cursor-not-allowed
                 transition-colors font-medium"
              >
                <Download size={18} />
                Download Excel
              </button>
            )}
          </div>
        </header>

        {error && (
          <div className="mb-8 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 animate-in fade-in slide-in-from-top-2">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            icon={Users}
            label="Applications"
            value={totalApps}
            subtext={`${totalWindows} windows tracked`}
            color="blue"
          />
          <StatsCard
            icon={Clock}
            label="Total Time"
            value={`${totalHours}h ${totalMinutes}m`}
            subtext="Active usage today"
            color="purple"
          />
          <StatsCard
            icon={TrendingUp}
            label="Most Used"
            value={mostUsedApp}
            subtext={dailyData[0]?.totalDuration || "0m"}
            color="green"
          />
          <StatsCard
            icon={BarChart2}
            label="Timeline Events"
            value={timelineData.length}
            subtext="Hourly activity logs"
            color="orange"
          />
        </div>

        {/* Main Table */}
        <div className="mb-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              Application Usage
            </h2>
            <p className="text-slate-400">
              Detailed breakdown of application activity and time tracking
            </p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 rounded-xl border border-white/10 bg-slate-900/50">
              <div className="w-12 h-12 border-4 border-slate-700 border-t-blue-500 rounded-full animate-spin mb-4"></div>
              <p className="text-slate-400">Loading activity data...</p>
            </div>
          ) : dailyData.length > 0 ? (
            <DataTable data={dailyData} />
          ) : (
            <div className="flex flex-col items-center justify-center py-20 rounded-xl border border-white/10 bg-slate-900/50">
              <div className="w-20 h-20 rounded-full bg-slate-800/50 flex items-center justify-center mb-4">
                <BarChart2 size={40} className="text-slate-600" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                No Activity Data
              </h3>
              <p className="text-slate-400">
                No tracked activity found for the selected date
              </p>
            </div>
          )}
        </div>

        {/* Timeline */}
        {/* Activity Timeline */}
        {timelineData.length > 0 && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">
                Activity Timeline
              </h2>
              <p className="text-slate-400">
                Activity distribution throughout the day
              </p>
            </div>

            <div className="space-y-4">
              {timelineData.map((item, idx) => {
                // ✅ FIX: use bucket instead of hour
                const bucketDate = new Date(item.bucket);

                // Format time
                const timeLabel = bucketDate.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                });

                return (
                  <div
                    key={idx}
                    className="grid grid-cols-[120px_1fr] gap-6 p-6 rounded-xl border border-white/10 bg-slate-900/50 hover:bg-slate-800/30 transition-colors"
                  >
                    {/* Time Label */}
                    <div className="flex items-center gap-2 font-mono text-lg font-bold text-white">
                      <Clock size={16} className="text-blue-400" />
                      <span>{timeLabel}</span>
                    </div>

                    {/* Apps */}
                    <div className="flex flex-wrap gap-3">
                      {item.apps?.map((act, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between gap-4 px-4 py-2.5 rounded-lg bg-slate-800/50 border border-white/5 hover:border-white/10 transition-colors min-w-[220px]"
                        >
                          <span className="text-sm font-medium text-white">
                            {act.app}
                          </span>

                          <span className="text-xs font-mono text-slate-400">
                            {Math.floor(act.totalDuration / 60)}m{" "}
                            {act.totalDuration % 60}s
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;
